import { View, StyleSheet } from "react-native";
import { useState, useEffect } from "react";
import DefaultLayout from "../layouts/DefaultLayout.web";
import CustomText from "../components/common/general/CustomText/CustomText.js";
import { useToast } from "../contexts/Toast/ToastProvider";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { useGetTenantSchemaQuery, useGetTenantByIdQuery, GetTenantByIdDocument } from "@axio/graphql/generated/graphql";
import { ScrollView } from "react-native-gesture-handler";
import { NavigationProp, RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import Loader from "../components/common/status/Loader/Loader";
import Divider from "../components/common/layout/Divider/Divider";
import CustomModal from "../components/common/general/CustomModal/CustomModal";
import { useUpdateTenantMutation } from "@axio/graphql/generated/graphql";
import SamlConfig from "../containers/tenantDashboard/SamlConfig";
import DynamicFormContainer from "../containers/tenantDashboard/DynamicFormContainer";
import { useAuth } from "../hooks/useAuth";

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => deepEqual(v, b[i]));
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }
  return false;
};

export default function TenantSettingsDashboardView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Tenant Settings">>();
  const params = route.params;

  const [tenantSchema, setTenantSchema] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const [originalValues, setOriginalValues] = useState<any>({});
  const [values, setValues] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { showToast } = useToast();
  const { meData } = useAuth();

  const { data: schemaData } = useGetTenantSchemaQuery();
  const { data: tenantData } = useGetTenantByIdQuery({ variables: { id: params.id } });

  const [updateTenant, { error: updateTenantError }] = useUpdateTenantMutation({
    refetchQueries: [
      {
        query: GetTenantByIdDocument,
        variables: { id: params.id },
      },
    ],
  });

  useEffect(() => {
    if (schemaData?.getTenantSchema?.data) {
      setTenantSchema(JSON.parse(schemaData.getTenantSchema.data));
    }
  }, [schemaData]);

  useEffect(() => {
    if (tenantData?.getTenantById?.data?.[0]) {
      const initial = tenantData.getTenantById.data[0];
      setOriginalValues(initial);
      setValues(initial);
    }
  }, [tenantData]);

  const handleChange = (key: string, val: any) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const openModal = () => {
    if (values.tenantName && values.tenantName !== values.tenantName.toLowerCase()) {
      showToast({
        message: "Tenant Name must be all lowercase.",
        type: "warning",
      });
      return;
    }
    if (values.tenantDomain && values.tenantDomain !== values.tenantDomain.toLowerCase()) {
      showToast({
        message: "Tenant Domain must be all lowercase.",
        type: "warning",
      });
      return;
    }
    setSubmitError("");
    setVisible(true);
  };
  const closeModal = () => {
    setSubmitError("");
    setVisible(false);
  };

  const handleGoBack = () => {
    navigation.navigate("Tenant Dashboard");
  };

  const handleSubmit = async () => {
    const diff = Object.keys(values).reduce((acc: any, key) => {
      if (!deepEqual(originalValues[key], values[key])) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    const updatePayload = {
      ...diff,
      tenantName: values.tenantName,
      tenantDomain: values.tenantDomain,
    };

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const { data } = await updateTenant({
        variables: {
          id: params.id,
          tenant: updatePayload,
        },
      });
      if (data?.updateTenant?.success) {
        if (data.updateTenant.message) {
          showToast({
            message: data.updateTenant.message,
            type: "warning",
          });
        }
        closeModal();
        navigation.navigate("Tenant Dashboard");
      } else {
        setSubmitError(data?.updateTenant?.message || "Failed to save settings. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!tenantSchema || !tenantData) {
    return <Loader />;
  }

  const changedKeys = Object.keys(values).filter((key) => !deepEqual(originalValues[key], values[key]));
  const protectedArrayValues = meData?.me?.email ? { adminEmails: [meData.me.email] } : undefined;

  return (
    <DefaultLayout pageHasChat={false}>
      <CustomButton
        text="← Back to Tenant Dashboard"
        onPress={handleGoBack}
        useTranslationText={false}
        styleType="transparent"
        size="s"
        style={styles.backButton}
      />
      <CustomSpacing size="m" />
      <CustomText
        role="header"
        text="Tenant Settings"
        size="l"
        textType="display"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="xxl" />
      <CustomSpacing size="xxl" />
      <SamlConfig tenantId={params.id} tenantDomain={values.tenantDomain} />
      <CustomSpacing size="xxl" />
      <CustomSpacing size="xxl" />
      <CustomText
        role="header"
        text={`${values.tenantDomain} settings - general form`}
        size="xxl"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="l" />

      <CustomSpacing size="l" />

      <ScrollView contentContainerStyle={styles.formContainer} tabIndex={0}>
        <DynamicFormContainer
          schema={tenantSchema.definitions.Tenant}
          originalValues={originalValues}
          values={values}
          onValueChange={handleChange}
          onSavePressed={openModal}
          saveButtonText="Save General Form Changes"
          saveButtonStyleType="lightGreen"
          protectedArrayValues={protectedArrayValues}
        />
      </ScrollView>

      <CustomModal visible={visible} onRequestClose={closeModal} showCloseButton title="Confirm Changes">
        <CustomText role="header" text="You are about to update:" size="m" weight="medium" useTranslationText={false} />
        <Divider size="m" />
        <ScrollView style={styles.modalList} tabIndex={0}>
          {changedKeys.map((key) => (
            <View key={key} style={styles.modalItem}>
              <CustomText role="header" text={key} size="m" weight="medium" useTranslationText={false} />
              <CustomText
                role="header"
                text={JSON.stringify(values[key])}
                size="m"
                style={{ color: "#d9534f" }}
                useTranslationText={false}
              />
              <Divider size="s" />
            </View>
          ))}
        </ScrollView>
        {(updateTenantError || submitError) && (
          <>
            <CustomText
              role="header"
              text={submitError || "Error updating tenant. Please retry."}
              size="m"
              style={{ color: "#d9534f" }}
              useTranslationText={false}
            />
            <Divider size="m" />
          </>
        )}
        <CustomButton
          text={isSubmitting ? "Saving..." : "Confirm"}
          styleType="lightGreen"
          onPress={handleSubmit}
          disabled={isSubmitting}
        />
        <CustomSpacing size="m" />
        <CustomButton text="Cancel" styleType="danger" onPress={closeModal} />
      </CustomModal>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  backButton: {
    alignSelf: "flex-start",
  },
  formContainer: {
    paddingBottom: 40,
  },
  modalList: {
    maxHeight: 200,
    marginVertical: 8,
  },
  modalItem: {
    marginBottom: 8,
  },
});
